import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DeleteIconButton } from '../../../shared/components/delete-icon-button/delete-icon-button';
import { EditIconButton } from '../../../shared/components/edit-icon-button/edit-icon-button';
import { CallCenterChipComponent, CallCenterChipConfig } from '../../../shared/components/call-center-chip/call-center-chip.component';

import { CalendarEvent } from '../../../interfaces/calendar-event.interface';

export interface CalendarEventDetailDialogData {
  event: CalendarEvent;
  currentUserId: number;
}

export interface CalendarEventDetailResult {
  action: 'edit' | 'delete' | 'statusChange' | 'openCompanyStatus';
  data?: string;
}

// Egy info sor a leírásban (pl. Adószám, Cím, Főtevékenység stb.)
interface DescriptionInfoLine {
  icon: string;
  label: string;
  value: string;
}

// Egy tisztségviselő bejegyzés: név + (érvényes) telefonok / emailek
interface DescriptionOfficial {
  name: string;
  phones: string[];
  emails: string[];
}

// A szerkesztett (cég-bázisú) leírás strukturált alakja
interface ParsedDescription {
  infoLines: DescriptionInfoLine[];
  companyPhones: string[];
  companyEmails: string[];
  notes: string | null;
  officials: DescriptionOfficial[];
  hasStructure: boolean; // ha false, akkor sima szövegként jelenítjük meg
}

// Ismert info sorok prefixei és a hozzájuk tartozó Material ikonok / címkék
const INFO_LINE_PREFIXES: Array<{ prefix: string; icon: string; label: string }> = [
  { prefix: '🔢 Adószám:', icon: 'pin', label: 'Adószám' },
  { prefix: '📋 Cégjegyzékszám:', icon: 'description', label: 'Cégjegyzékszám' },
  { prefix: '📍 Cím:', icon: 'location_on', label: 'Cím' },
  { prefix: '🗺️ Megye:', icon: 'map', label: 'Megye' },
  { prefix: '💼 Főtevékenység:', icon: 'work', label: 'Főtevékenység' },
  { prefix: '💰 Nettó árbevétel:', icon: 'payments', label: 'Nettó árbevétel' },
  { prefix: '👥 Alkalmazottak:', icon: 'groups', label: 'Alkalmazottak' },
];

@Component({
  selector: 'app-calendar-event-detail-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatTooltipModule,
    DeleteIconButton,
    EditIconButton,
    CallCenterChipComponent,
  ],
  templateUrl: './calendar-event-detail-dialog.component.html',
  styleUrls: ['./calendar-event-detail-dialog.component.scss'],
})
export class CalendarEventDetailDialogComponent {
  event: CalendarEvent;
  currentUserId: number;
  parsedDescription: ParsedDescription;

  constructor(
    private dialogRef: MatDialogRef<CalendarEventDetailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CalendarEventDetailDialogData,
  ) {
    this.event = data.event;
    this.currentUserId = data.currentUserId;
    this.parsedDescription = this.parseDescription(this.event.description);
  }

  get isCalendarEvent(): boolean {
    return this.event.source === 'calendar_event';
  }

  get isReminder(): boolean {
    return this.event.source === 'reminder';
  }

  get isOwnReminder(): boolean {
    return this.isReminder && this.event.user_id === this.currentUserId;
  }

  get eventTypeLabel(): string {
    switch (this.event.event_type) {
      case 'appointment': return 'Időpont';
      case 'meeting': return 'Találkozó';
      case 'reminder': return 'Emlékeztető';
      default: return 'Egyéb';
    }
  }

  get statusLabel(): string {
    switch (this.event.status) {
      case 'scheduled': return 'Tervezett';
      case 'completed': return 'Teljesített';
      case 'cancelled': return 'Lemondva';
      case 'pending': return 'Függőben';
      case 'dismissed': return 'Elutasítva';
      default: return this.event.status || '';
    }
  }

  get formattedStart(): string {
    if (!this.event.start) return '';
    const options: Intl.DateTimeFormatOptions = this.event.allDay
      ? { year: 'numeric', month: 'long', day: 'numeric' }
      : { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(this.event.start).toLocaleString('hu-HU', options);
  }

  get formattedEnd(): string {
    if (!this.event.end) return '';
    const options: Intl.DateTimeFormatOptions = this.event.allDay
      ? { year: 'numeric', month: 'long', day: 'numeric' }
      : { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(this.event.end).toLocaleString('hu-HU', options);
  }

  private static readonly KUM_STATUS_LABELS: { [key: string]: string } = {
    'elso_targyalasra_var': 'Első tárgyalásra vár'
  };

  getKumStatusLabel(status: string): string {
    return CalendarEventDetailDialogComponent.KUM_STATUS_LABELS[status] || status;
  }

  createKumStatusChipConfig(statusName: string): CallCenterChipConfig {
    return {
      text: this.getKumStatusLabel(statusName),
      type: 'status'
    };
  }

  // Tisztségviselő / telefon / email chip konfiguráció - mindig zöld (validstatus=true),
  // mert a backend csak az érvényes elérhetőségeket küldi a leírásba.
  createContactChipConfig(text: string, type: 'official' | 'phone' | 'email'): CallCenterChipConfig {
    return {
      text,
      type,
      validStatus: true,
    };
  }

  /**
   * A naptár esemény leírásának feldolgozása.
   * A buildCompanyDescription() által generált formátumot ismeri fel,
   * de szabad szöveg esetén hasStructure=false marad és sima szövegként jelenik meg.
   */
  private parseDescription(description: string | null): ParsedDescription {
    const result: ParsedDescription = {
      infoLines: [],
      companyPhones: [],
      companyEmails: [],
      notes: null,
      officials: [],
      hasStructure: false,
    };

    if (!description) {
      return result;
    }

    const lines = description.split('\n');
    let inOfficialsSection = false;
    let currentOfficial: DescriptionOfficial | null = null;
    const flushOfficial = () => {
      if (currentOfficial) {
        result.officials.push(currentOfficial);
        currentOfficial = null;
      }
    };

    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed === '') continue;

      // Tisztségviselők szekció kezdete
      if (trimmed.startsWith('👤 Tisztségviselők')) {
        inOfficialsSection = true;
        result.hasStructure = true;
        continue;
      }

      if (inOfficialsSection) {
        // Új tisztségviselő bejegyzés (•-tal kezdődik)
        if (trimmed.startsWith('•')) {
          flushOfficial();
          currentOfficial = {
            name: trimmed.replace(/^•\s*/, '').trim(),
            phones: [],
            emails: [],
          };
          continue;
        }
        // Tisztségviselő telefonjai
        if (trimmed.startsWith('📞') && currentOfficial) {
          currentOfficial.phones = this.splitContacts(trimmed.replace(/^📞\s*/, ''));
          continue;
        }
        // Tisztségviselő emailjei
        if (trimmed.startsWith('📧') && currentOfficial) {
          currentOfficial.emails = this.splitContacts(trimmed.replace(/^📧\s*/, ''));
          continue;
        }
        // Bármilyen ettől eltérő sor: kilépünk a tisztségviselők szekcióból
        flushOfficial();
        inOfficialsSection = false;
        // Hadd próbálja a többi szabály is feldolgozni
      }

      // Megjegyzés sor (📝 Megjegyzés: ...)
      if (trimmed.startsWith('📝 Megjegyzés:')) {
        result.notes = trimmed.substring('📝 Megjegyzés:'.length).trim();
        result.hasStructure = true;
        continue;
      }

      // Cég közvetlen telefonjai / emailjei (validstatus=true a backendben)
      if (trimmed.startsWith('📞 Cég tel.:')) {
        result.companyPhones = this.splitContacts(trimmed.substring('📞 Cég tel.:'.length));
        result.hasStructure = true;
        continue;
      }
      if (trimmed.startsWith('📧 Cég email:')) {
        result.companyEmails = this.splitContacts(trimmed.substring('📧 Cég email:'.length));
        result.hasStructure = true;
        continue;
      }

      // Egyéb info sorok (Adószám, Cím, Megye stb.)
      const known = INFO_LINE_PREFIXES.find(p => trimmed.startsWith(p.prefix));
      if (known) {
        const value = trimmed.substring(known.prefix.length).trim();
        result.infoLines.push({ icon: known.icon, label: known.label, value });
        result.hasStructure = true;
        continue;
      }
    }

    flushOfficial();
    return result;
  }

  private splitContacts(raw: string): string[] {
    return raw.split(',').map(s => s.trim()).filter(s => s.length > 0);
  }

  onEdit(): void {
    this.dialogRef.close({ action: 'edit' } as CalendarEventDetailResult);
  }

  onDelete(): void {
    this.dialogRef.close({ action: 'delete' } as CalendarEventDetailResult);
  }

  onStatusChange(status: string): void {
    this.dialogRef.close({ action: 'statusChange', data: status } as CalendarEventDetailResult);
  }

  onOpenCompanyStatus(): void {
    this.dialogRef.close({ action: 'openCompanyStatus' } as CalendarEventDetailResult);
  }

  onClose(): void {
    this.dialogRef.close(null);
  }
}
